import React, { useEffect, useState, useCallback, useMemo } from "react";
import useApi from "@/hooks/useApi";
import ReportExportButton from "@/components/ReportExportButton";

// ── Types ──────────────────────────────────────────────────────────────────

interface SlaSummary {
  period: { days: number; targetSeconds: number };
  total: number;
  within: number;
  breached: number;
  complianceRate: number;
  complianceRateChange: number;
  avgFirstResponseSecs: number;
  p90FirstResponseSecs: number;
  monthly: { period: string; label: string; total: number; within: number; breached: number; complianceRate: number }[];
}

interface RoiSummary {
  period: { start: string; end: string; days: number };
  inputs: { agentCostPerHour: number; avgHandleMinutes: number };
  headline: {
    totalConversations: number;
    botDeflections: number;
    deflectionRate: number;
    hoursSaved: number;
    estimatedSavings: number;
    aiSkillExecutions: number;
    resolutionRate: number;
    avgFirstResponseSecs: number;
    humanHandledConversations: number;
  };
  trends: {
    deflectionRateChange: number;
    resolutionRateChange: number;
    responseTimeChange: number | null;
    conversationVolumeChange: number;
  };
  monthlyBreakdown: MonthRow[];
  topSkills: SkillRow[];
  channelBreakdown: ChannelRow[];
}

interface MonthRow {
  period: string;
  label: string;
  total: number;
  botHandled: number;
  humanHandled: number;
  resolved: number;
  deflectionRate: number;
  resolutionRate: number;
  avgResponseSecs: number;
}

interface SkillRow { skill: string; calls: number }
interface ChannelRow { channel: string; conversations: number; botHandled: number; deflectionPct: number }

// ── Helpers ────────────────────────────────────────────────────────────────

function fmtMoney(n: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
}
function fmtNum(n: number) { return new Intl.NumberFormat("en-US").format(n); }
function fmtSecs(s: number) {
  if (s < 60) return `${Math.round(s)}s`;
  return `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
}

function TrendBadge({ value, invert = false, unit = "%" }: { value: number | null; invert?: boolean; unit?: string }) {
  if (value == null) return null;
  const good = invert ? value < 0 : value > 0;
  const neutral = value === 0;
  const color = neutral ? "bg-slate-100 text-slate-500"
    : good ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700";
  const arrow = neutral ? "" : good ? "↑ " : "↓ ";
  const label = `${arrow}${Math.abs(value)}${unit} vs prior period`;
  return (
    <span className={`inline-block text-[11px] font-medium px-2 py-0.5 rounded-full ${color}`}>
      {label}
    </span>
  );
}

// ── Headline KPI card ──────────────────────────────────────────────────────

function KpiCard({
  label, value, sub, iconClass, accent, trend, trendInvert, trendUnit
}: {
  label: string;
  value: string | number;
  sub?: string;
  iconClass: string;
  accent: string;
  trend?: number | null;
  trendInvert?: boolean;
  trendUnit?: string;
}) {
  return (
    <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-5 flex flex-col gap-2 shadow-sm">
      <div className="flex items-center gap-2">
        <div className={`rounded-lg p-2 shrink-0 ${accent}`}>
          <i className={`${iconClass} text-sm`} aria-hidden="true" />
        </div>
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</span>
      </div>
      <div className="text-3xl font-bold text-slate-800 dark:text-slate-100 leading-tight">{value}</div>
      {sub && <div className="text-sm text-slate-500 dark:text-slate-400">{sub}</div>}
      {trend != null && (
        <TrendBadge value={trend} invert={trendInvert} unit={trendUnit ?? "%"} />
      )}
    </div>
  );
}

// ── Mini bar chart (CSS) ───────────────────────────────────────────────────

function MiniBar({ value, max, color }: { value: number; max: number; color: string }) {
  const pct = max > 0 ? Math.max(2, (value / max) * 100) : 0;
  return (
    <div className="h-full w-full bg-slate-100 dark:bg-slate-700 rounded-sm relative overflow-hidden">
      <div
        className={`absolute bottom-0 left-0 right-0 ${color} rounded-sm transition-all duration-500`}
        style={{ height: `${pct}%` }}
      />
    </div>
  );
}

// ── Channel source labels ──────────────────────────────────────────────────

const CHANNEL_LABELS: Record<string, string> = {
  Web: "Web Chat", TwilioSms: "SMS", TwilioIvr: "IVR / Voice",
  WhatsApp: "WhatsApp", Facebook: "Facebook", Instagram: "Instagram",
  Twitter: "X / Twitter", Email: "Email", Ticket: "Ticket"
};

// ── Main component ─────────────────────────────────────────────────────────

export default function ValueReport() {
  const api = useApi();

  const [days, setDays] = useState(30);

  const exportStartDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - days);
    return d;
  }, [days]);
  const exportEndDate = useMemo(() => new Date(), []);
  const [costPerHour, setCostPerHour] = useState(25);
  const [handleMins, setHandleMins] = useState(8);
  const [slaTargetDraft, setSlaTargetDraft] = useState(300);
  const [slaTarget, setSlaTarget] = useState(300);

  // Debounce slaTarget so keystrokes don't fire an API call per digit
  useEffect(() => {
    const t = setTimeout(() => setSlaTarget(slaTargetDraft), 500);
    return () => clearTimeout(t);
  }, [slaTargetDraft]);
  const [data, setData] = useState<RoiSummary | null>(null);
  const [slaData, setSlaData] = useState<SlaSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const [roiRes, slaRes] = await Promise.all([
        api.messaging.get(
          `api/admin/RoiReport/Summary?days=${days}&agentCostPerHour=${costPerHour}&avgHandleMinutes=${handleMins}`
        ),
        api.messaging.get(
          `api/admin/RoiReport/SlaCompliance?days=${days}&targetSeconds=${slaTarget}`
        ),
      ]);
      if (!roiRes.ok) throw new Error();
      const json = await roiRes.json();
      setData(json);
      if (slaRes.ok) setSlaData(await slaRes.json());
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [days, costPerHour, handleMins, slaTarget]);

  useEffect(() => { load(); }, [load]);

  const h = data?.headline;
  const t = data?.trends;
  const maxMonthTotal = Math.max(...(data?.monthlyBreakdown ?? []).map(m => m.total), 1);

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-8 print:p-2">

      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">Value Report</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
            Concrete ROI — deflections, time saved, and AI impact for the selected period.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 items-center print:hidden">
          {/* Period selector */}
          {[30, 60, 90].map(d => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                days === d
                  ? "bg-cornflower-blue-600 text-white border-cornflower-blue-600"
                  : "bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600 hover:border-cornflower-blue-400"
              }`}
            >
              {d}d
            </button>
          ))}
          <button
            onClick={() => window.print()}
            className="ml-2 px-3 py-1.5 rounded-lg text-sm font-medium bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 flex items-center gap-1.5"
          >
            <i className="fa-solid fa-print text-xs" aria-hidden="true" />
            Print / PDF
          </button>
          <ReportExportButton
            defaultStartDate={exportStartDate}
            defaultEndDate={exportEndDate}
            reportType="account-overview"
          />
        </div>
      </div>

      {/* ── Cost calculator inputs ── */}
      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/50 rounded-xl p-4 flex flex-wrap gap-6 items-center print:hidden">
        <div className="flex items-center gap-2">
          <i className="fa-solid fa-calculator text-amber-600 dark:text-amber-400" aria-hidden="true" />
          <span className="text-sm font-semibold text-amber-800 dark:text-amber-300">Savings Calculator</span>
        </div>
        <label className="flex items-center gap-2 text-sm text-amber-800 dark:text-amber-300">
          Agent cost / hr
          <span className="font-medium">$</span>
          <input
            type="number"
            min={1} max={500} step={1}
            value={costPerHour}
            onChange={e => setCostPerHour(Number(e.target.value))}
            className="w-20 px-2 py-1 rounded border border-amber-300 dark:border-amber-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-sm"
            aria-label="Agent cost per hour in dollars"
          />
        </label>
        <label className="flex items-center gap-2 text-sm text-amber-800 dark:text-amber-300">
          Avg handle time
          <input
            type="number"
            min={1} max={120} step={1}
            value={handleMins}
            onChange={e => setHandleMins(Number(e.target.value))}
            className="w-16 px-2 py-1 rounded border border-amber-300 dark:border-amber-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-sm"
            aria-label="Average handle time in minutes"
          />
          <span className="font-medium">min</span>
        </label>
        <button
          onClick={load}
          disabled={loading}
          className="px-4 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium transition-colors disabled:opacity-50"
        >
          Recalculate
        </button>
        <label className="flex items-center gap-2 text-sm text-amber-800 dark:text-amber-300">
          SLA target
          <input
            type="number"
            min={30} max={3600} step={30}
            value={slaTargetDraft}
            onChange={e => setSlaTargetDraft(Number(e.target.value))}
            className="w-20 px-2 py-1 rounded border border-amber-300 dark:border-amber-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-sm"
            aria-label="SLA target in seconds"
          />
          <span className="font-medium">s</span>
        </label>
        <span className="text-xs text-amber-600 dark:text-amber-400 italic">
          Savings = (bot deflections × handle time) × agent hourly rate
        </span>
      </div>

      {/* ── Loading / error states ── */}
      {loading && (
        <div className="flex items-center justify-center py-16 text-slate-400">
          <i className="fa-solid fa-circle-notch fa-spin text-2xl" aria-hidden="true" />
        </div>
      )}
      {error && !loading && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-xl p-4 text-red-700 dark:text-red-300 text-sm flex gap-2 items-center">
          <i className="fa-solid fa-triangle-exclamation" aria-hidden="true" />
          Could not load report data. Check your connection and try again.
          <button onClick={load} className="ml-auto underline">Retry</button>
        </div>
      )}

      {!loading && !error && h && (
        <>
          {/* ── Headline KPIs ── */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard
              label="Bot Deflections"
              value={fmtNum(h.botDeflections)}
              sub={`${h.deflectionRate}% of all conversations`}
              iconClass="fa-solid fa-robot text-violet-600 dark:text-violet-400"
              accent="bg-violet-100 dark:bg-violet-900/30"
              trend={t?.deflectionRateChange}
            />
            <KpiCard
              label="Agent Hours Saved"
              value={`${fmtNum(Math.round(h.hoursSaved))}h`}
              sub={`at ${handleMins} min avg handle`}
              iconClass="fa-solid fa-clock text-blue-600 dark:text-blue-400"
              accent="bg-blue-100 dark:bg-blue-900/30"
            />
            <KpiCard
              label="Estimated Savings"
              value={fmtMoney(h.estimatedSavings)}
              sub={`at $${costPerHour}/hr agent cost`}
              iconClass="fa-solid fa-circle-dollar-to-slot text-emerald-600 dark:text-emerald-400"
              accent="bg-emerald-100 dark:bg-emerald-900/30"
            />
            <KpiCard
              label="AI Skill Executions"
              value={fmtNum(h.aiSkillExecutions)}
              sub="CRM lookups, orders, bookings, etc."
              iconClass="fa-solid fa-bolt text-amber-600 dark:text-amber-400"
              accent="bg-amber-100 dark:bg-amber-900/30"
            />
          </div>

          {/* ── Secondary KPIs ── */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard
              label="Total Conversations"
              value={fmtNum(h.totalConversations)}
              sub={`${fmtNum(h.humanHandledConversations)} human-handled`}
              iconClass="fa-solid fa-comments text-slate-500"
              accent="bg-slate-100 dark:bg-slate-700"
              trend={t?.conversationVolumeChange}
            />
            <KpiCard
              label="Resolution Rate"
              value={`${h.resolutionRate}%`}
              sub="conversations closed successfully"
              iconClass="fa-solid fa-circle-check text-teal-600 dark:text-teal-400"
              accent="bg-teal-100 dark:bg-teal-900/30"
              trend={t?.resolutionRateChange}
            />
            <KpiCard
              label="Avg First Response"
              value={fmtSecs(h.avgFirstResponseSecs)}
              sub="from chat start to first reply"
              iconClass="fa-solid fa-reply text-sky-600 dark:text-sky-400"
              accent="bg-sky-100 dark:bg-sky-900/30"
              trend={t?.responseTimeChange ?? undefined}
              trendInvert
              trendUnit="s"
            />
            <div className="bg-gradient-to-br from-cornflower-blue-600 to-violet-600 rounded-xl p-5 flex flex-col justify-between shadow-sm">
              <div className="text-xs font-semibold uppercase tracking-wide text-cornflower-blue-100">
                Deflection Rate
              </div>
              <div className="text-5xl font-black text-white mt-2">{h.deflectionRate}%</div>
              <div className="text-cornflower-blue-100 text-sm">
                {data?.period.days}d window
              </div>
            </div>
          </div>

          {/* ── Monthly trend chart ── */}
          {data!.monthlyBreakdown.length > 0 && (
            <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-5 shadow-sm">
              <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-4">
                Monthly Trend — Conversations vs Bot Deflections
              </h2>
              <div className="overflow-x-auto">
                <div className="flex items-end gap-3 min-w-[320px]" style={{ height: 160 }}>
                  {data!.monthlyBreakdown.map(m => (
                    <div key={m.period} className="flex flex-col items-center gap-1 flex-1 min-w-[40px]">
                      <div className="flex gap-0.5 items-end w-full" style={{ height: 120 }}>
                        <div className="flex-1" title={`${m.total} total`}>
                          <MiniBar value={m.total} max={maxMonthTotal} color="bg-slate-300 dark:bg-slate-600" />
                        </div>
                        <div className="flex-1" title={`${m.botHandled} bot-handled`}>
                          <MiniBar value={m.botHandled} max={maxMonthTotal} color="bg-violet-400 dark:bg-violet-500" />
                        </div>
                      </div>
                      <span className="text-[10px] text-slate-500 dark:text-slate-400 text-center leading-tight">
                        {m.label.replace(" 20", " '")}
                      </span>
                      <span className="text-[10px] font-semibold text-violet-600 dark:text-violet-400">
                        {m.deflectionRate}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex gap-4 mt-3">
                <span className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                  <span className="w-3 h-3 rounded-sm bg-slate-300 dark:bg-slate-600 inline-block" />
                  Total conversations
                </span>
                <span className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                  <span className="w-3 h-3 rounded-sm bg-violet-400 dark:bg-violet-500 inline-block" />
                  Bot deflected
                </span>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

            {/* ── Top AI Skills ── */}
            {data!.topSkills.length > 0 && (
              <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-5 shadow-sm">
                <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-4">
                  <i className="fa-solid fa-bolt text-amber-500 mr-1.5" aria-hidden="true" />
                  Top AI Skills
                </h2>
                <div className="space-y-2">
                  {data!.topSkills.map((s, i) => {
                    const maxCalls = data!.topSkills[0]?.calls ?? 1;
                    const pct = Math.max(4, (s.calls / maxCalls) * 100);
                    return (
                      <div key={s.skill} className="flex items-center gap-3">
                        <span className="text-xs text-slate-400 w-4 text-right">{i + 1}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-0.5">
                            <span className="text-sm text-slate-700 dark:text-slate-200 truncate font-medium">
                              {s.skill}
                            </span>
                            <span className="text-xs text-slate-500 dark:text-slate-400 shrink-0 ml-2">
                              {fmtNum(s.calls)}×
                            </span>
                          </div>
                          <div className="h-1.5 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-amber-400 dark:bg-amber-500 rounded-full"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── Channel breakdown ── */}
            {data!.channelBreakdown.length > 0 && (
              <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-5 shadow-sm">
                <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-4">
                  <i className="fa-solid fa-tower-broadcast text-sky-500 mr-1.5" aria-hidden="true" />
                  Channel Breakdown
                </h2>
                <div className="space-y-3">
                  {data!.channelBreakdown.map(c => (
                    <div key={c.channel}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
                          {CHANNEL_LABELS[c.channel] ?? c.channel}
                        </span>
                        <div className="flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400">
                          <span>{fmtNum(c.conversations)} total</span>
                          <span className="font-semibold text-violet-600 dark:text-violet-400">
                            {c.deflectionPct}% deflected
                          </span>
                        </div>
                      </div>
                      <div className="h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden flex">
                        <div
                          className="h-full bg-violet-400 dark:bg-violet-500 rounded-l-full"
                          style={{ width: `${c.deflectionPct}%` }}
                          title={`${c.botHandled} bot-handled`}
                        />
                        <div
                          className="h-full bg-slate-300 dark:bg-slate-600"
                          style={{ width: `${100 - c.deflectionPct}%` }}
                          title={`${c.conversations - c.botHandled} human-handled`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* ── Monthly detail table ── */}
          {data!.monthlyBreakdown.length > 0 && (
            <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-5 shadow-sm overflow-x-auto">
              <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-4">Monthly Detail</h2>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide border-b border-slate-100 dark:border-slate-700">
                    <th className="text-left pb-2 pr-4">Month</th>
                    <th className="text-right pb-2 pr-4">Total</th>
                    <th className="text-right pb-2 pr-4">Bot Handled</th>
                    <th className="text-right pb-2 pr-4">Human</th>
                    <th className="text-right pb-2 pr-4">Deflection %</th>
                    <th className="text-right pb-2 pr-4">Resolution %</th>
                    <th className="text-right pb-2">Avg Response</th>
                  </tr>
                </thead>
                <tbody>
                  {data!.monthlyBreakdown.map(m => (
                    <tr key={m.period} className="border-b border-slate-50 dark:border-slate-700/50 hover:bg-slate-50 dark:hover:bg-slate-700/30">
                      <td className="py-2 pr-4 font-medium text-slate-700 dark:text-slate-200">{m.label}</td>
                      <td className="py-2 pr-4 text-right text-slate-600 dark:text-slate-300">{fmtNum(m.total)}</td>
                      <td className="py-2 pr-4 text-right text-violet-600 dark:text-violet-400 font-medium">{fmtNum(m.botHandled)}</td>
                      <td className="py-2 pr-4 text-right text-slate-500 dark:text-slate-400">{fmtNum(m.humanHandled)}</td>
                      <td className="py-2 pr-4 text-right">
                        <span className="inline-block bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 px-2 py-0.5 rounded text-xs font-semibold">
                          {m.deflectionRate}%
                        </span>
                      </td>
                      <td className="py-2 pr-4 text-right">
                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${
                          m.resolutionRate >= 80 ? "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300"
                          : m.resolutionRate >= 60 ? "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300"
                          : "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300"
                        }`}>
                          {m.resolutionRate}%
                        </span>
                      </td>
                      <td className="py-2 text-right text-slate-500 dark:text-slate-400 text-xs">{fmtSecs(m.avgResponseSecs)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* ── SLA Compliance ── */}
          {slaData && slaData.total > 0 && (
            <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-5 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                  <i className="fa-solid fa-gauge-high text-teal-500 mr-1.5" aria-hidden="true" />
                  SLA Compliance — First Response within {slaTarget}s
                </h2>
                <TrendBadge value={slaData.complianceRateChange} />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
                <div className="text-center">
                  <div className={`text-3xl font-black ${slaData.complianceRate >= 90 ? "text-emerald-600" : slaData.complianceRate >= 70 ? "text-amber-500" : "text-red-500"}`}>
                    {slaData.complianceRate}%
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">Compliance Rate</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-slate-700 dark:text-slate-200">{fmtNum(slaData.within)}</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">Within SLA</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-500">{fmtNum(slaData.breached)}</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">Breached</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-sky-600 dark:text-sky-400">{fmtSecs(slaData.p90FirstResponseSecs)}</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">P90 First Response</div>
                </div>
              </div>
              {slaData.monthly.length > 1 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide border-b border-slate-100 dark:border-slate-700">
                        <th className="text-left pb-2 pr-4">Month</th>
                        <th className="text-right pb-2 pr-4">Total</th>
                        <th className="text-right pb-2 pr-4">Within SLA</th>
                        <th className="text-right pb-2">Compliance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {slaData.monthly.map(m => (
                        <tr key={m.period} className="border-b border-slate-50 dark:border-slate-700/50 hover:bg-slate-50 dark:hover:bg-slate-700/30">
                          <td className="py-2 pr-4 font-medium text-slate-700 dark:text-slate-200">{m.label}</td>
                          <td className="py-2 pr-4 text-right text-slate-500 dark:text-slate-400">{fmtNum(m.total)}</td>
                          <td className="py-2 pr-4 text-right text-emerald-600 dark:text-emerald-400 font-medium">{fmtNum(m.within)}</td>
                          <td className="py-2 text-right">
                            <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${
                              m.complianceRate >= 90 ? "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300"
                              : m.complianceRate >= 70 ? "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300"
                              : "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300"
                            }`}>
                              {m.complianceRate}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ── No data state ── */}
          {h.totalConversations === 0 && (
            <div className="bg-slate-50 dark:bg-slate-800/50 border border-dashed border-slate-300 dark:border-slate-600 rounded-xl p-8 text-center">
              <i className="fa-solid fa-chart-line text-4xl text-slate-300 dark:text-slate-600 mb-3 block" aria-hidden="true" />
              <p className="text-slate-500 dark:text-slate-400 text-sm">
                No conversation data found for the selected period. Once chats start flowing, this report will populate automatically.
              </p>
            </div>
          )}

          {/* ── Period footnote ── */}
          <p className="text-xs text-slate-400 dark:text-slate-500 text-center">
            Showing data for {new Date(data!.period.start).toLocaleDateString()} – {new Date(data!.period.end).toLocaleDateString()}.
            {" "}Trends compare to the prior {data!.period.days}-day window.
            {" "}Savings are estimates based on your configured agent cost and handle time.
          </p>
        </>
      )}
    </div>
  );
}
